import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from 'store/hooks'

const PROJECT_STORAGE_KEY = 'selectedProjectId'

const readPersistedProjectId = (): string | null => {
  if (typeof globalThis === 'undefined' || !globalThis.localStorage) return null
  try {
    return globalThis.localStorage.getItem(PROJECT_STORAGE_KEY)
  } catch {
    return null
  }
}

const writePersistedProjectId = (id: string | null): void => {
  if (typeof globalThis === 'undefined' || !globalThis.localStorage) return
  try {
    if (id) globalThis.localStorage.setItem(PROJECT_STORAGE_KEY, id)
    else globalThis.localStorage.removeItem(PROJECT_STORAGE_KEY)
  } catch {
    // Private-browsing / disabled storage — in-memory value still wins.
  }
}

interface ProjectSelectionState {
  projectId: string | null;
}

const initialState: ProjectSelectionState = {
  projectId: readPersistedProjectId()
}

const projectSelectionSlice = createSlice({
  name: 'projectSelection',
  initialState,
  reducers: {
    setProjectId: (state, action: PayloadAction<string | null>) => {
      state.projectId = action.payload
    },
    clearProjectId: (state) => {
      state.projectId = null
    }
  }
})

const { setProjectId, clearProjectId } = projectSelectionSlice.actions

// Structurally typed so we don't import RootState (which is derived from
// rootReducer, which imports this slice — that would loop).
const selectProjectId = (
  state: { projectSelection: ProjectSelectionState }
): string | null => state.projectSelection.projectId

// ── Public hooks ───────────────────────────────────────────────────────

export const useSelectedProjectId = (): string | null =>
  useAppSelector(selectProjectId)

export const useSetSelectedProjectId = (): ((id: string | null) => void) => {
  const dispatch = useAppDispatch()
  return useCallback(
    (id: string | null) => {
      writePersistedProjectId(id)
      dispatch(setProjectId(id))
    },
    [dispatch]
  )
}

export const useClearSelectedProjectId = (): (() => void) => {
  const dispatch = useAppDispatch()
  return useCallback(() => {
    writePersistedProjectId(null)
    dispatch(clearProjectId())
  }, [dispatch])
}

export default projectSelectionSlice
